import { OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { assertNever, CONTEST_ENTRY_AWARD_STATUSES, Datastore, EntryIdFilterOption, EntryIdSortOption, isDefined, OrderByDirection, pluckList, toNumber, VISIBLE_ENTRIES_DEFAULT_FILTER, } from '@freelancer/datastore';
import { MessagingChat } from '@freelancer/messaging-chat';
import { BackgroundColor } from '@freelancer/ui/page-layout';
import { HOUR } from '@freelancer/ui/relative-time';
import { ContestStatusApi, EntryStatusApi, } from 'api-typings/contests/contests';
import { ContextTypeApi, ThreadTypeApi, } from 'api-typings/messages/messages_types';
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
export var EntryFilterOption;
(function (EntryFilterOption) {
    EntryFilterOption["ALL_ENTRIES"] = "All Entries";
    EntryFilterOption["ACTIVE"] = "Active";
    EntryFilterOption["TOP"] = "Top";
    // NO_FEEDBACK = 'No Feedback',
    EntryFilterOption["REJECTED"] = "Rejected";
    EntryFilterOption["WITHDRAWN"] = "Withdrawn";
})(EntryFilterOption || (EntryFilterOption = {}));
export var EntrySortOption;
(function (EntrySortOption) {
    EntrySortOption["DEFAULT"] = "Default";
    EntrySortOption["RATING"] = "Rating";
    EntrySortOption["TIME_SUBMITTED"] = "Time Submitted";
    // FREELANCER_RATING = 'Freelancer Rating',
    EntrySortOption["LIKES"] = "Likes";
})(EntrySortOption || (EntrySortOption = {}));
var ContestViewComponent = /** @class */ (function () {
    function ContestViewComponent(siteName, auth, activatedRoute, datastore, messagingChat, router, title) {
        this.siteName = siteName;
        this.auth = auth;
        this.activatedRoute = activatedRoute;
        this.datastore = datastore;
        this.messagingChat = messagingChat;
        this.router = router;
        this.title = title;
        this.BackgroundColor = BackgroundColor;
        this.ENTRY_LIST_PAGE_SIZE = 24;
        this.TOP_ENTRY_MIN_RATING = 4;
        this.EXTEND_BANNER_CONTEST_MAX_HOURS = 24;
        this.EXTEND_BANNER_MIN_ENTRY_COUNT = 20;
        this.AWARD_BANNER_GRACE_PERIOD_DAYS = 30;
        this.currentPageSubject$ = new Rx.BehaviorSubject(1);
        this.currentPage$ = this.currentPageSubject$.asObservable();
        this.currentEntryFilterSubject$ = new Rx.BehaviorSubject(EntryFilterOption.ALL_ENTRIES);
        this.currentEntryFilter$ = this.currentEntryFilterSubject$
            .asObservable()
            .pipe(distinctUntilChanged());
        this.currentEntrySortSubject$ = new Rx.BehaviorSubject(EntrySortOption.DEFAULT);
        this.currentEntrySort$ = this.currentEntrySortSubject$
            .asObservable()
            .pipe(distinctUntilChanged());
        this.isOnMultiAwardSubject$ = new Rx.BehaviorSubject(false);
        this.isOnMultiAward$ = this.isOnMultiAwardSubject$.asObservable();
        this.multiAwardEntries = [];
        this.multiAwardEntriesSubject$ = new Rx.BehaviorSubject([]);
        this.multiAwardEntries$ = this.multiAwardEntriesSubject$.asObservable();
        this.multiAwardFreelancers = [];
        this.multiAwardFreelancersSubject$ = new Rx.BehaviorSubject([]);
        this.multiAwardFreelancers$ = this.multiAwardFreelancersSubject$.asObservable();
    }
    ContestViewComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.userId$ = this.auth.getUserId().pipe(map(function (id) { return toNumber(id); }));
        this.user$ = this.datastore.document('users', this.userId$).valueChanges();
        var contestId$ = this.activatedRoute.params.pipe(map(function (params) { return toNumber(params.contestId); }));
        this.contestDocument = this.datastore.document('contestViewContests', contestId$);
        this.contest$ = this.contestDocument.valueChanges();
        this.contestLoaded$ = this.contestDocument.status$.pipe(map(function (status) { return status.ready; }));
        this.contestError$ = this.contestDocument.status$.pipe(map(function (status) { return !!status.error; }));
        this.contestFilesCollection = this.datastore.collection('contestFiles', function (query) { return query.where('contestId', '==', contestId$); });
        this.contestFiles$ = this.contestFilesCollection.valueChanges();
        this.skills$ = this.datastore.collection('skills').valueChanges();
        this.currentLimit$ = this.currentPage$.pipe(map(function (currentPage) { return currentPage * _this.ENTRY_LIST_PAGE_SIZE; }));
        this.entriesCollection = this.datastore.collection('contestViewEntries', function (query) {
            return Rx.combineLatest([
                _this.currentEntryFilter$,
                _this.currentEntrySort$,
            ]).pipe(map(function (_a) {
                var currentEntryFilter = _a[0], currentEntrySort = _a[1];
                var queryBuild = query.where('contestId', '==', contestId$);
                // Queries should be the same with
                // ContestProjectEntriesMySqlDAO::getFilteredAndSortedEntryIds
                switch (currentEntryFilter) {
                    case EntryFilterOption.ALL_ENTRIES:
                        queryBuild = queryBuild.where('status', 'in', VISIBLE_ENTRIES_DEFAULT_FILTER);
                        break;
                    case EntryFilterOption.ACTIVE:
                        queryBuild = queryBuild.where('status', 'in', [
                            EntryStatusApi.PENDING,
                            EntryStatusApi.ACTIVE,
                            EntryStatusApi.WON,
                            EntryStatusApi.WON_CLOSED,
                            EntryStatusApi.WON_RUNNER_UP,
                            EntryStatusApi.WON_BRONZE,
                            EntryStatusApi.WON_SILVER,
                            EntryStatusApi.WON_GOLD,
                            EntryStatusApi.CHOSEN,
                            EntryStatusApi.BOUGHT,
                        ]);
                        break;
                    case EntryFilterOption.TOP:
                        queryBuild = _this.getTopRatedEntriesQuery(queryBuild);
                        break;
                    case EntryFilterOption.WITHDRAWN:
                        queryBuild = queryBuild.where('status', 'in', [
                            EntryStatusApi.WITHDRAWN,
                            EntryStatusApi.WITHDRAWN_ELIMINATED,
                        ]);
                        break;
                    case EntryFilterOption.REJECTED:
                        queryBuild = queryBuild.where('status', 'in', [
                            EntryStatusApi.ELIMINATED,
                            EntryStatusApi.WITHDRAWN_ELIMINATED,
                        ]);
                        break;
                    default:
                        assertNever(currentEntryFilter);
                }
                switch (currentEntrySort) {
                    case EntrySortOption.DEFAULT:
                        break;
                    case EntrySortOption.RATING:
                        queryBuild = queryBuild
                            .orderBy('rating', OrderByDirection.DESC)
                            .orderBy('statusNumber', OrderByDirection.DESC)
                            .orderBy('number', OrderByDirection.DESC);
                        break;
                    case EntrySortOption.TIME_SUBMITTED:
                        queryBuild = queryBuild
                            .orderBy('number', OrderByDirection.DESC)
                            .orderBy('statusNumber', OrderByDirection.DESC)
                            .orderBy('rating', OrderByDirection.DESC);
                        break;
                    case EntrySortOption.LIKES:
                        queryBuild = queryBuild
                            .orderBy('likeCount', OrderByDirection.DESC)
                            .orderBy('statusNumber', OrderByDirection.DESC)
                            .orderBy('rating', OrderByDirection.DESC)
                            .orderBy('number', OrderByDirection.DESC);
                        break;
                    default:
                        assertNever(currentEntrySort);
                }
                return queryBuild.limit(_this.currentLimit$);
            }));
        });
        this.entries$ = this.entriesCollection.valueChanges();
        this.entriesLoaded$ = this.entriesCollection.status$.pipe(map(function (status) { return status.ready; }));
        this.entriesError$ = this.entriesCollection.status$.pipe(map(function (status) { return !!status.error; }));
        this.visibleEntries$ = Rx.combineLatest([
            this.entries$,
            this.currentPage$,
            this.currentLimit$,
        ]).pipe(map(function (_a) {
            var entries = _a[0], currentPage = _a[1], currentLimit = _a[2];
            return entries.slice((currentPage - 1) * _this.ENTRY_LIST_PAGE_SIZE, currentLimit);
        }));
        this.entryIdsCollection = this.datastore.collection('contestViewEntryIds', function (query) {
            return Rx.combineLatest([
                _this.currentEntryFilter$,
                _this.currentEntrySort$,
            ]).pipe(map(function (_a) {
                var currentEntryFilter = _a[0], currentEntrySort = _a[1];
                var queryBuild = query.where('contestId', '==', contestId$);
                switch (currentEntryFilter) {
                    case EntryFilterOption.ALL_ENTRIES:
                        queryBuild = queryBuild.where('filter', '==', EntryIdFilterOption.DEFAULT);
                        break;
                    case EntryFilterOption.ACTIVE:
                        queryBuild = queryBuild.where('filter', '==', EntryIdFilterOption.ACTIVE);
                        break;
                    case EntryFilterOption.TOP:
                        queryBuild = queryBuild.where('filter', '==', EntryIdFilterOption.TOP);
                        break;
                    case EntryFilterOption.WITHDRAWN:
                        queryBuild = queryBuild.where('filter', '==', EntryIdFilterOption.WITHDRAWN);
                        break;
                    case EntryFilterOption.REJECTED:
                        queryBuild = queryBuild.where('filter', '==', EntryIdFilterOption.REJECTED);
                        break;
                    default:
                        assertNever(currentEntryFilter);
                }
                switch (currentEntrySort) {
                    case EntrySortOption.DEFAULT:
                        queryBuild = queryBuild.where('sort', '==', EntryIdSortOption.DEFAULT);
                        break;
                    case EntrySortOption.RATING:
                        queryBuild = queryBuild.where('sort', '==', EntryIdSortOption.RATING);
                        break;
                    case EntrySortOption.TIME_SUBMITTED:
                        queryBuild = queryBuild.where('sort', '==', EntryIdSortOption.TIME_SUBMITTED);
                        break;
                    case EntrySortOption.LIKES:
                        queryBuild = queryBuild.where('sort', '==', EntryIdSortOption.LIKES);
                        break;
                    default:
                        assertNever(currentEntrySort);
                }
                return queryBuild;
            }));
        });
        this.entryIds$ = this.entryIdsCollection.valueChanges().pipe(map(function (contestEntryIds) { return contestEntryIds[0]; }), filter(isDefined));
        this.currentEntryFilterSubscription = this.currentEntryFilter$.subscribe(function () { return _this.jumpToPage(1); });
        this.currentEntrySortSubscription = this.currentEntrySort$.subscribe(function (e) {
            _this.jumpToPage(1);
        });
        this.numberOfPages$ = this.entryIds$.pipe(map(function (contestViewEntryIds) {
            return Math.ceil(contestViewEntryIds.entryIds.length / _this.ENTRY_LIST_PAGE_SIZE);
        }));
        var engagementDocument = this.datastore.document('contestEngagement', contestId$);
        this.engagement$ = engagementDocument.valueChanges();
        var awardedEntriesCollection = this.datastore.collection('contestViewEntries', function (query) {
            return query
                .where('contestId', '==', contestId$)
                .where('status', 'in', CONTEST_ENTRY_AWARD_STATUSES);
        });
        // Getting the first awarded entry is done on the observable instead of
        // in the datastore query because `orderBy` doesn't support nullable field.
        // `timeWon` is a nullable field.
        // FIXME: Sort in datastore query after T84381 is implemented
        this.awardedEntry$ = awardedEntriesCollection.valueChanges().pipe(map(function (awardedEntries) {
            return awardedEntries.slice().sort(function (previousEntry, currentEntry) {
                if (previousEntry.timeWon && currentEntry.timeWon) {
                    return previousEntry.timeWon - currentEntry.timeWon;
                }
                if (previousEntry.timeWon) {
                    return -1;
                }
                if (currentEntry.timeWon) {
                    return 1;
                }
                return 0;
            })[0];
        }));
        this.awardedEntryError$ = awardedEntriesCollection.status$.pipe(map(function (status) { return !!status.error; }));
        var awardedFreelancerDocument = this.datastore.document('users', this.awardedEntry$.pipe(filter(isDefined), map(function (awardedEntry) { return awardedEntry.ownerId; })));
        this.awardedFreelancer$ = awardedFreelancerDocument.valueChanges();
        this.awardedFreelancerError$ = awardedFreelancerDocument.status$.pipe(map(function (status) { return !!status.error; }));
        this.isContestOwner$ = Rx.combineLatest([this.userId$, this.contest$]).pipe(map(function (_a) {
            var userId = _a[0], contest = _a[1];
            return userId === contest.ownerId;
        }));
        // ds.objects() doesn't expose a status$ stream so we opted to use ds.collection here
        var freelancersCollection = this.datastore.collection('users', function (query) {
            return query.where('id', 'in', _this.visibleEntries$.pipe(pluckList('ownerId')));
        });
        this.freelancers$ = freelancersCollection.valueChanges();
        this.freelancersLoaded$ = freelancersCollection.status$.pipe(map(function (status) { return status.ready; }));
        this.freelancersError$ = freelancersCollection.status$.pipe(map(function (status) { return !!status.error; }));
        var currencyId$ = this.contest$.pipe(map(function (contest) { return contest.currency.id; }));
        var contestFeesCollection = this.datastore.collection('contestFees', function (query) {
            return query
                .where('contestId', '==', contestId$)
                .where('currencyId', '==', currencyId$);
        });
        this.contestFees$ = contestFeesCollection.valueChanges().pipe(map(function (contestFees) { return contestFees[0]; }), filter(isDefined));
        this.contestFeesLoaded$ = contestFeesCollection.status$.pipe(map(function (status) { return status.ready; }));
        this.contestFeesError$ = contestFeesCollection.status$.pipe(map(function (status) { return !!status.error; }));
        var taxDocument = this.datastore.document('tax', this.userId$);
        this.tax$ = taxDocument.valueChanges();
        this.taxError$ = taxDocument.status$.pipe(map(function (status) { return !!status.error; }));
        var isNavigationEndEvent$ = this.router.events.pipe(map(function (event) { return event instanceof NavigationEnd; }), startWith(true));
        var emitForSetTitle$ = Rx.combineLatest([
            this.contest$.pipe(map(function (contest) { return contest.title; })),
            isNavigationEndEvent$.pipe(filter(function (value) { return value; })),
        ]).pipe(map(function (_a) {
            var title = _a[0], _ = _a[1];
            return title;
        }));
        this.setTitleSubscription = emitForSetTitle$.subscribe(function (title) {
            return _this.title.setTitle(title + " | " + _this.siteName);
        });
        var currentTimestamp = Date.now();
        var hasTopRatedEntries$ = this.datastore
            .collection('contestViewEntries', function (query) {
            var queryBuild = query.where('contestId', '==', contestId$);
            queryBuild = _this.getTopRatedEntriesQuery(queryBuild).limit(1);
            return queryBuild;
        })
            .valueChanges()
            .pipe(map(function (topRatedEntryArray) { return topRatedEntryArray.length !== 0; }));
        this.showExtendBanner$ = Rx.combineLatest([
            this.isContestOwner$,
            this.awardedEntry$,
            this.contest$,
            hasTopRatedEntries$,
        ]).pipe(map(function (_a) {
            var isContestOwner = _a[0], awardedEntry = _a[1], contest = _a[2], hasTopRatedEntries = _a[3];
            if (contest.timeEnded === undefined ||
                contest.entryCount === undefined) {
                return false;
            }
            var timeRemainingInHours = (contest.timeEnded - currentTimestamp) / (HOUR * 1000);
            return (isContestOwner &&
                !awardedEntry &&
                (!contest.upgrades || !contest.upgrades.urgent) &&
                timeRemainingInHours > 0 &&
                timeRemainingInHours <= _this.EXTEND_BANNER_CONTEST_MAX_HOURS &&
                (contest.entryCount > _this.EXTEND_BANNER_MIN_ENTRY_COUNT ||
                    !hasTopRatedEntries));
        }));
        this.timeLeftToAward$ = this.contest$.pipe(map(function (contest) {
            if (contest.timeEnded) {
                var awardDeadline = new Date(contest.timeEnded);
                awardDeadline.setDate(awardDeadline.getDate() + _this.AWARD_BANNER_GRACE_PERIOD_DAYS);
                return awardDeadline.getTime() - currentTimestamp;
            }
            return null;
        }));
        this.showAwardBanner$ = Rx.combineLatest([
            this.isContestOwner$,
            this.awardedEntry$,
            this.contest$,
            this.timeLeftToAward$,
        ]).pipe(map(function (_a) {
            var isContestOwner = _a[0], awardedEntry = _a[1], contest = _a[2], timeLeftToAward = _a[3];
            return !!(isContestOwner &&
                !awardedEntry &&
                contest.status === ContestStatusApi.PENDING &&
                contest.timeEnded &&
                currentTimestamp >= contest.timeEnded &&
                timeLeftToAward &&
                timeLeftToAward > 0 &&
                contest.entryCount &&
                contest.entryCount > 0);
        }));
        this.hasInvoices$ = this.datastore
            .document('contestHasInvoices', contestId$)
            .valueChanges();
    };
    ContestViewComponent.prototype.getTopRatedEntriesQuery = function (query) {
        return query
            .where('status', 'in', [
            EntryStatusApi.PENDING,
            EntryStatusApi.ACTIVE,
            EntryStatusApi.WON_RUNNER_UP,
            EntryStatusApi.WON_BRONZE,
            EntryStatusApi.WON_SILVER,
            EntryStatusApi.WON_GOLD,
        ])
            .where('rating', '>=', this.TOP_ENTRY_MIN_RATING);
    };
    ContestViewComponent.prototype.jumpToPage = function (pageNumber) {
        this.currentPageSubject$.next(pageNumber);
    };
    ContestViewComponent.prototype.setEntryFilter = function (entryFilter) {
        this.currentEntryFilterSubject$.next(entryFilter);
    };
    ContestViewComponent.prototype.setEntrySort = function (entrySort) {
        this.currentEntrySortSubject$.next(entrySort);
    };
    ContestViewComponent.prototype.startChat = function (entry, origin) {
        this.messagingChat.startChat({
            userIds: [entry.ownerId],
            threadType: ThreadTypeApi.PRIVATE_CHAT,
            origin: origin,
            context: {
                type: ContextTypeApi.CONTEST,
                id: entry.contestId,
            },
        });
    };
    /**
     * We don't want to retrieve unnecessary data so we do lazy loading for
     * interestedFreelancers.
     *
     * @returns Rx.Observable<ReadonlyArray<ContestInterestedFreelancer>>
     */
    ContestViewComponent.prototype.getInterestedFreelancersObservable = function () {
        var _this = this;
        if (this.interestedFreelancers$ === undefined) {
            this.interestedFreelancers$ = this.datastore
                .collection('contestInterestedFreelancers', function (query) {
                return query.where('contestId', '==', _this.contest$.pipe(map(function (contest) { return contest.id; })));
            })
                .valueChanges();
        }
        return this.interestedFreelancers$;
    };
    ContestViewComponent.prototype.updateContestDetails = function (contestChanges) {
        return this.contestDocument.update(contestChanges);
    };
    ContestViewComponent.prototype.updateContestFiles = function (newFiles, deletedFileIds) {
        var _this = this;
        var createPromises = [];
        var deletePromises = [];
        if (newFiles) {
            createPromises = newFiles.map(function (file) {
                return _this.datastore.createObject('contestFiles', file);
            });
        }
        if (deletedFileIds) {
            deletePromises = deletedFileIds.map(function (id) {
                return _this.contestFilesCollection.remove(id);
            });
        }
        return Promise.all(createPromises.concat(deletePromises));
    };
    ContestViewComponent.prototype.handleMultiAwardSelectToggle = function (contestMultiAwardSelection) {
        if (contestMultiAwardSelection.selected) {
            this.addMultiAwardEntry(contestMultiAwardSelection);
        }
        else if (this.multiAwardEntries.length > 1) {
            this.removeMultiAwardEntry(contestMultiAwardSelection);
        }
        else {
            this.handleMultiAwardCancel();
        }
    };
    ContestViewComponent.prototype.addMultiAwardEntry = function (contestMultiAwardSelection) {
        this.multiAwardEntries = this.multiAwardEntries.concat([
            contestMultiAwardSelection.entry,
        ]);
        this.multiAwardEntriesSubject$.next(this.multiAwardEntries);
        this.multiAwardFreelancers = this.multiAwardFreelancers.concat([
            contestMultiAwardSelection.freelancer,
        ]);
        this.multiAwardFreelancersSubject$.next(this.multiAwardFreelancers);
    };
    ContestViewComponent.prototype.removeMultiAwardEntry = function (contestMultiAwardSelection) {
        // Multi-award entries and their respective freelancers are mapped
        // via matching indexes
        var removeElementIndex = this.multiAwardEntries.indexOf(contestMultiAwardSelection.entry);
        this.multiAwardEntries = this.multiAwardEntries.filter(function (entry) { return entry.id !== contestMultiAwardSelection.entry.id; });
        this.multiAwardEntriesSubject$.next(this.multiAwardEntries);
        // Ensures removal of only one instance of the freelancer per
        // one removed multi-awarded entry since `multiAwardFreelancers`
        // can contain non-unique elements, e.g. some multi-awarded entries
        // belonging to one user
        this.multiAwardFreelancers = this.multiAwardFreelancers
            .slice(0, removeElementIndex)
            .concat(this.multiAwardFreelancers.slice(removeElementIndex + 1));
        this.multiAwardFreelancersSubject$.next(this.multiAwardFreelancers);
    };
    ContestViewComponent.prototype.handleMultiAwardCancel = function () {
        this.isOnMultiAwardSubject$.next(false);
        this.multiAwardEntries = [];
        this.multiAwardEntriesSubject$.next(this.multiAwardEntries);
        this.multiAwardFreelancers = [];
        this.multiAwardFreelancersSubject$.next(this.multiAwardFreelancers);
    };
    ContestViewComponent.prototype.handleMultiAwardShow = function () {
        this.isOnMultiAwardSubject$.next(true);
    };
    ContestViewComponent.prototype.ngOnDestroy = function () {
        if (this.setTitleSubscription) {
            this.setTitleSubscription.unsubscribe();
        }
        if (this.currentEntryFilterSubscription) {
            this.currentEntryFilterSubscription.unsubscribe();
        }
        if (this.currentEntrySortSubscription) {
            this.currentEntrySortSubscription.unsubscribe();
        }
    };
    return ContestViewComponent;
}());
export { ContestViewComponent };
